/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { Typography2 } from '@coursera/cds-core';

import EmbeddedPartnerLogo, {
  EmbeddedPartnerLogoSkeleton,
} from 'bundles/common/lms-authoring/components/EmbeddedPartnerLogo';
import EmbeddedPartnerName, {
  EmbeddedPartnerNameSkeleton,
} from 'bundles/common/lms-authoring/components/EmbeddedPartnerName';
import { TrimmedEmbeddedPartnerName } from 'bundles/common/lms-authoring/components/TrimmedEmbeddedPartnerName';
import type { CourseTypeMetadataFragment } from 'bundles/common/lms-authoring/queries/__generated__/getCourseAndPartnerDataById';
import { useGetEmbeddedSourceCourseQuery } from 'bundles/common/lms-authoring/queries/useGetEmbeddedSourceCourseQuery';
import {
  EmbeddedSourceCourseNameOptions,
  type EmbeddedSourceCourseNameOptionsType,
} from 'bundles/common/lms-authoring/types/EmbeddedSourceCourseNameOptions';
import type { SourceCoursePartners } from 'bundles/common/lms-authoring/types/SourceCoursePartners';

export type { SourceCoursePartners };

export type Props = {
  embeddedContentSourceCourseId: string;
  partners?: SourceCoursePartners;
  courseName?: string;
  courseSlug?: string;
  includeCourseName?: EmbeddedSourceCourseNameOptionsType;
  originalName?: string;
  loading?: boolean;
  boldPartnerName?: boolean;
  className?: string;
  trimmedAttributionProperties?: {
    fullContentLink: string;
  };
};

const styles = {
  attributionContainer: css`
    display: flex;
    flex-direction: row;
    align-items: center;
  `,
  name: css`
    flex: initial;
    min-width: 0;
  `,
  logo: css`
    flex: none;
  `,
};

const isShortFormContent = (courseMetadata?: CourseTypeMetadataFragment) => {
  return courseMetadata?.metadata.__typename === 'Course_ShortFormContentMetadata';
};

export const EmbeddedSourceCourseAttributionSkeleton: React.FC<{
  className?: string;
  includeCourseName?: EmbeddedSourceCourseNameOptionsType;
}> = React.memo(({ className, includeCourseName }) => (
  <React.Fragment>
    <div data-testid="loading" css={styles.attributionContainer} className={className}>
      <EmbeddedPartnerLogoSkeleton />
      <EmbeddedPartnerNameSkeleton includeCourseName={includeCourseName} />
    </div>
    {includeCourseName === EmbeddedSourceCourseNameOptions.BELOW && (
      <EmbeddedPartnerNameSkeleton includeCourseName={includeCourseName} />
    )}
  </React.Fragment>
));

const EmbeddedSourceCourseAttribution: React.FC<Props> = ({
  embeddedContentSourceCourseId,
  partners: inputPartners,
  courseName: inputCourseName,
  courseSlug: inputCourseSlug,
  includeCourseName,
  originalName,
  loading: loadingOverride = false,
  boldPartnerName = false,
  className,
  trimmedAttributionProperties,
}) => {
  const { sourceCourse, loading, error } = useGetEmbeddedSourceCourseQuery(
    embeddedContentSourceCourseId,
    !!inputPartners
  );

  if (error) {
    return null;
  }

  if (loading || loadingOverride) {
    return <EmbeddedSourceCourseAttributionSkeleton includeCourseName={includeCourseName} className={className} />;
  }

  const partners = inputPartners ?? sourceCourse?.partners?.filter((partner) => !!partner) ?? [];
  if (partners.length < 1) {
    return null;
  }

  const courseName = inputCourseName || sourceCourse?.name;
  const courseSlug = inputCourseSlug || sourceCourse?.slug;
  return (
    <React.Fragment>
      <div css={styles.attributionContainer} className={`rc-EmbeddedSourceCourseAttribution ${className}`}>
        <EmbeddedPartnerLogo partners={partners} css={styles.logo} />

        {trimmedAttributionProperties ? (
          <TrimmedEmbeddedPartnerName
            partners={partners}
            courseName={courseName}
            originalName={originalName}
            fullContentLink={trimmedAttributionProperties.fullContentLink}
            css={styles.name}
            courseSlug={courseSlug}
          />
        ) : (
          <EmbeddedPartnerName
            partners={partners}
            boldPartnerName={boldPartnerName}
            includeCourseName={includeCourseName === EmbeddedSourceCourseNameOptions.INLINE}
            shortFormContent={isShortFormContent(sourceCourse?.courseTypeMetadata)}
            courseName={courseName}
            courseSlug={courseSlug}
            originalName={originalName}
            css={styles.name}
          />
        )}
      </div>
      {includeCourseName === EmbeddedSourceCourseNameOptions.BELOW && (
        <Typography2 data-testid="embedded-below-text-content" variant="subtitleMedium" component="h4">
          {sourceCourse?.name}
        </Typography2>
      )}
    </React.Fragment>
  );
};

export default EmbeddedSourceCourseAttribution;
